import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Zap, Moon, Sun, User, Mail, MapPin, Calendar, Users,
  Save, ChevronRight, CreditCard, Download, Shield, Sparkles, Crown,
  Copy, Gift, Share2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55+', 'Prefer not to say'];
const GENDERS = ['Male', 'Female', 'Prefer not to say'];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    state: '',
    age_range: '',
    gender: ''
  });
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    // Wait for auth to finish loading before redirecting
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth?mode=signin');
      return;
    }
    fetchProfile();
  }, [user, authLoading, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({
        name: response.data.name || '',
        state: response.data.state || '',
        age_range: response.data.age_range || '',
        gender: response.data.gender || ''
      });
      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'free': return <Shield className="w-5 h-5" />;
      case 'basic': return <Sparkles className="w-5 h-5" />;
      case 'pro': return <Crown className="w-5 h-5" />;
      case 'lifetime_pro': return <Gift className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0;
    return Math.min(100, (used / limit) * 100);
  };

  const formatLimit = (limit) => {
    if (limit === -1) return 'Unlimited';
    return limit.toString();
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
        <h1 className="text-2xl font-bold mb-6">My Profile</h1>

        {/* Subscription Status Card */}
        {subscription && (
          <div className="bg-card rounded-xl border border-border p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  subscription.tier === 'pro' || subscription.tier === 'lifetime_pro' ? 'bg-primary/20 text-primary' :
                  subscription.tier === 'basic' ? 'bg-blue-500/20 text-blue-500' :
                  'bg-secondary text-muted-foreground'
                }`}>
                  {getTierIcon(subscription.tier)}
                </div>
                <div>
                  <h3 className="font-semibold capitalize">
                    {subscription.tier === 'lifetime_pro' ? 'Lifetime Pro' : `${subscription.tier} Plan`}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {subscription.tier === 'free' ? 'Lifetime limits' : 
                     subscription.tier === 'lifetime_pro' ? 'Never expires' :
                     usage?.reset_date ? `Resets ${new Date(usage.reset_date).toLocaleDateString()}` : 'Active'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/pricing')}
              >
                {subscription.tier === 'free' ? 'Upgrade' : subscription.tier === 'lifetime_pro' ? 'View Plan' : 'Manage'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Usage Stats */}
            {usage && (
              <div className="space-y-3">
                {/* Actions (Combined Searches + Calculations) for Free users */}
                {subscription.tier === 'free' ? (
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Actions (Searches + Calculations)</span>
                      <span className="text-muted-foreground">
                        {(usage.scans_used || 0) + (usage.profit_calcs_used || 0)} / {formatLimit(usage.scans_limit)}
                      </span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${getUsagePercentage((usage.scans_used || 0) + (usage.profit_calcs_used || 0), usage.scans_limit)}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Searches */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Searches</span>
                        <span className="text-muted-foreground">
                          {usage.scans_used} / {formatLimit(usage.scans_limit)}
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${getUsagePercentage(usage.scans_used, usage.scans_limit)}%` }}
                        />
                      </div>
                    </div>

                    {/* Profit Calculations */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Profit Calculations</span>
                        <span className="text-muted-foreground">
                          {usage.profit_calcs_used} / {formatLimit(usage.profit_calcs_limit)}
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${getUsagePercentage(usage.profit_calcs_used, usage.profit_calcs_limit)}%` }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Inventory */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Inventory Items</span>
                    <span className="text-muted-foreground">
                      {usage.inventory_count} / {formatLimit(usage.inventory_limit)}
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${getUsagePercentage(usage.inventory_count, usage.inventory_limit)}%` }}
                    />
                  </div>
                </div>

                {/* Release Schedule - Only show for Pro users */}
                {(subscription.tier === 'pro' || subscription.tier === 'lifetime_pro') && (
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Release Schedule Items</span>
                      <span className="text-muted-foreground">
                        {usage.releases_count || 0} / {formatLimit(usage.releases_limit || -1)}
                      </span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${getUsagePercentage(usage.releases_count || 0, usage.releases_limit || 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Referral Code Section */}
        <div className="bg-card rounded-xl border border-border p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Gift className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold">Your Referral Code</h2>
              <p className="text-xs text-muted-foreground">Share with friends to earn rewards</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 px-4 py-3 bg-secondary rounded-lg font-mono text-lg font-bold tracking-wider text-center">
              {user?.referral_code || 'Loading...'}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 flex-shrink-0"
              onClick={() => {
                if (user?.referral_code) {
                  navigator.clipboard.writeText(user.referral_code);
                  toast.success('Referral code copied!');
                }
              }}
            >
              <Copy className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 flex-shrink-0"
              onClick={() => {
                const shareUrl = `${window.location.origin}/auth?mode=signup&ref=${user?.referral_code}`;
                if (user?.referral_code && navigator.share) {
                  navigator.share({
                    title: 'Join FlipValue',
                    text: `Use my referral code ${user.referral_code} to sign up for FlipValue!`,
                    url: shareUrl
                  }).catch(() => {});
                } else if (user?.referral_code) {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success('Referral link copied!');
                }
              }}
            >
              <Share2 className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex items-center justify-between px-3 py-2 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">Friends Referred</span>
            </div>
            <span className="font-bold text-primary">{user?.referral_count || 0}</span>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-card rounded-xl border border-border p-4 mb-6">
          <h2 className="font-semibold mb-4">Account Information</h2>
          
          {/* Email (read-only) */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-lg text-muted-foreground">
              <Mail className="w-4 h-4" />
              <span>{user?.email}</span>
            </div>
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5">Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Your name"
                className="pl-10"
              />
            </div>
          </div>

          {/* State */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5">State (optional)</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <select
                value={profile.state}
                onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select state</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Age Range */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5">Age Range (optional)</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <select
                value={profile.age_range}
                onChange={(e) => setProfile({ ...profile, age_range: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select age range</option>
                {AGE_RANGES.map((range) => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Gender */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5">Gender (optional)</label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <select
                value={profile.gender}
                onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select gender</option>
                {GENDERS.map((gender) => (
                  <option key={gender} value={gender}>{gender}</option>
                ))}
              </select>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mb-4">
            These optional details help us improve FlipValue anonymously. See our Privacy Policy for more info.
          </p>

          <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="space-y-2">
          <button
            onClick={() => navigate('/subscription')}
            className="w-full flex items-center justify-between p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
          >
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              <span>Manage Subscription</span>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          {subscription?.tier === 'pro' && (
            <button
              onClick={() => toast.info('Export feature coming soon!')}
              className="w-full flex items-center justify-between p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-muted-foreground" />
                <span>Export Data</span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
